import * as PlatformSocket from 'effect/unstable/socket/Socket'
import {
  Cause,
  Deferred,
  Duration,
  Effect,
  Fiber,
  Queue,
  Random,
  Ref,
  Result,
  Scope,
  Stream,
  SubscriptionRef
} from 'effect'
import {
  isHttpStatusError,
  isInvalidRequestError,
  isInvalidResponseError,
  isTimeoutError,
  isTransportError
} from '../fetch-errors'
import {
  computeBackoffMs,
  makeInitialModel,
  sortedJoinedTopics,
  type RuntimeModel,
  type SubscriptionHandler
} from './model'
import {
  decodePhoenixFrame,
  encodeHeartbeatFrame,
  encodeJoinFrame,
  encodeLeaveFrame,
  encodePushFrame,
  frameToEnvelope
} from './protocol'
import { makePlatformTransport } from './transport-platform'
import {
  type ConnectParamsError,
  type ConnectionState,
  type Envelope,
  PushError,
  type RealtimeLifecycleEvent,
  type RealtimeSocket,
  type RealtimeSocketConfig,
  type RealtimeSocketErroredEvent,
  type RealtimeTopicJoinStalledEvent,
  type RealtimeVisibility,
  type Topic
} from './types'

type RuntimeCommand =
  // External intent
  | { readonly _tag: 'ConnectRequested' }
  | { readonly _tag: 'DisconnectRequested' }
  | { readonly _tag: 'RejoinRequested'; readonly topic: Topic }
  | {
      readonly _tag: 'PushRequested'
      readonly topic: Topic
      readonly event: string
      readonly payload: unknown
      readonly deferred: Deferred.Deferred<unknown, PushError>
    }
  | { readonly _tag: 'LifecycleVisibilityChanged'; readonly visibility: RealtimeVisibility }
  | { readonly _tag: 'LifecyclePageHide'; readonly persisted: boolean }
  | { readonly _tag: 'LifecyclePageShow'; readonly persisted: boolean }
  // Transport lifecycle callbacks (generation-scoped to drop stale callbacks)
  | { readonly _tag: 'SocketOpened'; readonly generation: number }
  | {
      readonly _tag: 'SocketClosed'
      readonly generation: number
      readonly code: number
      readonly reason?: string
      readonly wasClean: boolean
    }
  | { readonly _tag: 'SocketErrored'; readonly generation: number; readonly error: unknown }
  | {
      readonly _tag: 'SocketFrameReceived'
      readonly generation: number
      readonly data: string | Uint8Array
    }
  // Internal timers
  | { readonly _tag: 'ReconnectTimerFired'; readonly generation: number }
  | { readonly _tag: 'ConnectOpenTimeoutFired'; readonly generation: number }
  | { readonly _tag: 'HeartbeatTimerFired'; readonly generation: number }
  | { readonly _tag: 'HeartbeatTimeoutFired'; readonly generation: number; readonly ref: string }
  | {
      readonly _tag: 'JoinTimeoutFired'
      readonly generation: number
      readonly topic: Topic
      readonly ref: string
    }
  | {
      readonly _tag: 'JoinRetryTimerFired'
      readonly generation: number
      readonly topic: Topic
      readonly attempt: number
    }
  | {
      readonly _tag: 'TopicJoinStallTimerFired'
      readonly topic: Topic
      readonly episode: number
    }
  | {
      readonly _tag: 'AddSubscription'
      readonly topic: Topic
      readonly subscriptionId: number
      readonly handler: SubscriptionHandler
    }
  | {
      readonly _tag: 'RemoveSubscription'
      readonly topic: Topic
      readonly subscriptionId: number
    }

const DEFAULTS = {
  reconnectBaseDelayMs: 500,
  reconnectMaxDelayMs: 5000,
  reconnectJitter: 0.2,
  connectOpenTimeoutMs: 15_000,
  heartbeatIntervalMs: 30_000,
  joinTimeoutMs: 10_000,
  joinRetryBaseDelayMs: 500,
  joinRetryMaxDelayMs: 5_000
} as const

/**
 * Realtime runtime architecture:
 * - All mutable state lives in `RuntimeModel` and is mutated only in the command loop.
 * - Every transport callback/timer carries a generation token so stale fibers are ignored.
 * - Connectivity/topic intent is declarative (`desiredConnected` + `desiredTopicRefCounts`).
 */
export function makeRealtimeSocket(
  config: RealtimeSocketConfig
): Effect.Effect<RealtimeSocket, never, Scope.Scope> {
  return Effect.gen(function* () {
    // Decode once at startup so every timer path uses canonical Duration values.
    const heartbeatInterval = Duration.fromInputUnsafe(
      config.heartbeatInterval ?? DEFAULTS.heartbeatIntervalMs
    )
    const heartbeatTimeout = Duration.fromInputUnsafe(config.heartbeatTimeout ?? heartbeatInterval)
    let topicJoinStallTimeout: Duration.Duration | null = null
    if (config.topicJoinStall) {
      topicJoinStallTimeout = Duration.fromInputUnsafe(config.topicJoinStall.timeout)
    }

    const normalized = {
      reconnectBaseDelayMs: config.reconnectBaseDelayMs ?? DEFAULTS.reconnectBaseDelayMs,
      reconnectMaxDelayMs: config.reconnectMaxDelayMs ?? DEFAULTS.reconnectMaxDelayMs,
      reconnectJitter: config.reconnectJitter ?? DEFAULTS.reconnectJitter,
      connectOpenTimeoutMs: config.connectOpenTimeoutMs ?? DEFAULTS.connectOpenTimeoutMs,
      heartbeatInterval,
      heartbeatTimeout,
      joinTimeoutMs: config.joinTimeoutMs ?? DEFAULTS.joinTimeoutMs,
      joinRetryBaseDelayMs: config.joinRetryBaseDelayMs ?? DEFAULTS.joinRetryBaseDelayMs,
      joinRetryMaxDelayMs: config.joinRetryMaxDelayMs ?? DEFAULTS.joinRetryMaxDelayMs,
      topicJoinStallTimeout
    }

    const makeTransport = config.makeTransport ?? makePlatformTransport

    const commands = yield* Queue.unbounded<RuntimeCommand>()
    const subscriptionIdRef = yield* Ref.make(1)

    const connectionStateRef = yield* SubscriptionRef.make<ConnectionState>({
      _tag: 'disconnected',
      userInitiated: false
    })
    const connectedTopicsRef = yield* SubscriptionRef.make<ReadonlyArray<Topic>>([])

    const offer = (command: RuntimeCommand) => Queue.offer(commands, command).pipe(Effect.asVoid)

    // Lifecycle events are funneled through the same queue as socket/timer events so ordering
    // stays deterministic (no out-of-band state mutation from browser callbacks).
    const offerLifecycleEvent = (event: RealtimeLifecycleEvent) => {
      switch (event._tag) {
        case 'VisibilityChanged':
          return offer({
            _tag: 'LifecycleVisibilityChanged',
            visibility: event.visibility
          })
        case 'PageHide':
          return offer({
            _tag: 'LifecyclePageHide',
            persisted: event.persisted
          })
        case 'PageShow':
          return offer({
            _tag: 'LifecyclePageShow',
            persisted: event.persisted
          })
      }
    }

    const publishConnectionState = (state: ConnectionState) =>
      SubscriptionRef.set(connectionStateRef, state)

    const publishConnectedTopics = (model: RuntimeModel) =>
      SubscriptionRef.set(connectedTopicsRef, sortedJoinedTopics(model))

    const reportUnexpectedCause = (context: string, cause: Cause.Cause<unknown>) => {
      if (Cause.hasInterruptsOnly(cause)) {
        return Effect.void
      }
      if (config.onDefect) {
        return config.onDefect({ context, error: Cause.squash(cause) })
      }
      return Effect.logError(`[realtime-runtime] ${context}\n${Cause.pretty(cause)}`)
    }

    const closeTransport = (
      transport: NonNullable<RuntimeModel['transport']>,
      code: number,
      reason: string,
      context: string
    ) =>
      transport.close(code, reason).pipe(
        Effect.timeoutOrElse({
          duration: '2 seconds',
          orElse: () => Effect.logWarning(`[realtime-runtime] ${context}: timed out`)
        }),
        Effect.catchTags({
          ConnectTransportError: (error) =>
            Effect.logWarning(`[realtime-runtime] ${context}: ${error.message}`)
        }),
        Effect.tapCause((cause) => reportUnexpectedCause(context, cause)),
        Effect.asVoid
      )

    const closeTransportWithoutBlocking = (
      transport: NonNullable<RuntimeModel['transport']>,
      code: number,
      reason: string,
      context: string
    ) =>
      closeTransport(transport, code, reason, context).pipe(
        Effect.forkScoped({ startImmediately: true }),
        Effect.asVoid
      )

    const interruptFiberIfAny = (fiber: Fiber.Fiber<void, never> | null) =>
      fiber ? Fiber.interrupt(fiber).pipe(Effect.asVoid) : Effect.void

    const cancelConnectOpenTimeout = (model: RuntimeModel) =>
      Effect.gen(function* () {
        yield* interruptFiberIfAny(model.connectOpenTimeoutFiber)
        model.connectOpenTimeoutFiber = null
      })

    const scheduleConnectOpenTimeout = (model: RuntimeModel, generation: number) =>
      Effect.gen(function* () {
        yield* cancelConnectOpenTimeout(model)
        if (normalized.connectOpenTimeoutMs <= 0) {
          return
        }

        const timeoutFiber = yield* Effect.sleep(
          Duration.millis(normalized.connectOpenTimeoutMs)
        ).pipe(
          Effect.andThen(offer({ _tag: 'ConnectOpenTimeoutFired', generation })),
          Effect.forkScoped({ startImmediately: true })
        )
        model.connectOpenTimeoutFiber = timeoutFiber
        yield* Effect.logDebug(
          `[realtime-runtime] armed connect-open timeout generation=${generation} timeoutMs=${normalized.connectOpenTimeoutMs}`
        )
      })

    const cancelHeartbeatTimers = (model: RuntimeModel) =>
      Effect.gen(function* () {
        yield* interruptFiberIfAny(model.heartbeatFiber)
        yield* interruptFiberIfAny(model.heartbeatTimeoutFiber)
        model.heartbeatFiber = null
        model.heartbeatTimeoutFiber = null
        model.pendingHeartbeatRef = null
      })

    const scheduleHeartbeat = (model: RuntimeModel) =>
      Effect.gen(function* () {
        if (!model.transport || model.connection._tag != 'connected') {
          return
        }

        yield* interruptFiberIfAny(model.heartbeatFiber)
        const heartbeatFiber = yield* Effect.sleep(normalized.heartbeatInterval).pipe(
          Effect.andThen(
            offer({
              _tag: 'HeartbeatTimerFired',
              generation: model.generation
            })
          ),
          Effect.forkScoped({ startImmediately: true })
        )
        model.heartbeatFiber = heartbeatFiber
      })

    const sendHeartbeat = (model: RuntimeModel) =>
      Effect.gen(function* () {
        const transport = model.transport
        if (!transport || model.connection._tag != 'connected') {
          return
        }
        if (model.pendingHeartbeatRef) {
          return
        }

        // Heartbeats use transport-level refs (not channel join refs).
        const ref = nextRef(model)
        model.pendingHeartbeatRef = ref

        const sent = yield* transport.send(encodeHeartbeatFrame(ref)).pipe(
          Effect.as(true),
          Effect.catchTag('ConnectTransportError', () =>
            Effect.gen(function* () {
              model.pendingHeartbeatRef = null
              if (!model.desiredConnected) {
                return false
              }

              const failedTransport = model.transport
              if (failedTransport) {
                // Drive reconnect through local close semantics so topic lifecycle resets are
                // deterministic even if transport callbacks are delayed or never emitted.
                model.generation += 1
                yield* handleSocketClose(model, {
                  _tag: 'SocketClosed',
                  generation: model.generation,
                  code: 1006,
                  reason: 'heartbeat send failure',
                  wasClean: false
                })
                yield* closeTransportWithoutBlocking(
                  failedTransport,
                  1000,
                  'heartbeat send failure',
                  'transport close failed after heartbeat send failure'
                )
              } else {
                yield* scheduleReconnect(model, model.generation, model.reconnectAttempt + 1)
              }
              return false
            })
          )
        )
        if (!sent) {
          return
        }

        yield* interruptFiberIfAny(model.heartbeatTimeoutFiber)
        const heartbeatTimeoutFiber = yield* Effect.sleep(normalized.heartbeatTimeout).pipe(
          Effect.andThen(
            offer({
              _tag: 'HeartbeatTimeoutFired',
              generation: model.generation,
              ref
            })
          ),
          Effect.forkScoped({ startImmediately: true })
        )
        model.heartbeatTimeoutFiber = heartbeatTimeoutFiber
      })

    const cancelTopicTimers = (model: RuntimeModel, topic: Topic) =>
      Effect.gen(function* () {
        const joinTimeoutFiber = model.joinTimeoutFibers.get(topic)
        if (joinTimeoutFiber) {
          yield* Fiber.interrupt(joinTimeoutFiber)
          model.joinTimeoutFibers.delete(topic)
        }

        const joinRetryFiber = model.joinRetryFibers.get(topic)
        if (joinRetryFiber) {
          yield* Fiber.interrupt(joinRetryFiber)
          model.joinRetryFibers.delete(topic)
        }
      })

    // Topic health is derived from the same serialized model as join/retry state:
    // every desired-but-unjoined topic owns one episode record and one timer. Timer
    // commands carry the episode token, so an interrupted timer can never report a
    // later episode. Joining, unsubscribing, or hiding the page clears the record;
    // returning visible therefore starts a fresh full window.
    const clearTopicJoinStall = (model: RuntimeModel, topic: Topic) =>
      Effect.gen(function* () {
        yield* interruptFiberIfAny(model.topicJoinStalls.get(topic)?.timer ?? null)
        model.topicJoinStalls.delete(topic)
      })

    const clearAllTopicJoinStalls = (model: RuntimeModel) =>
      Effect.gen(function* () {
        for (const stall of model.topicJoinStalls.values()) {
          yield* interruptFiberIfAny(stall.timer)
        }
        model.topicJoinStalls.clear()
      })

    const reconcileTopicJoinStalls = (model: RuntimeModel) =>
      Effect.gen(function* () {
        if (!config.topicJoinStall || !normalized.topicJoinStallTimeout) {
          return
        }

        if (model.visibility == 'hidden') {
          yield* clearAllTopicJoinStalls(model)
          return
        }

        const joinedTopics = new Set(sortedJoinedTopics(model))
        for (const topic of model.topicJoinStalls.keys()) {
          if (!model.desiredTopicRefCounts.has(topic) || joinedTopics.has(topic)) {
            yield* clearTopicJoinStall(model, topic)
          }
        }

        for (const topic of model.desiredTopicRefCounts.keys()) {
          if (joinedTopics.has(topic) || model.topicJoinStalls.has(topic)) {
            continue
          }

          const episode = model.nextTopicJoinStallEpisode
          model.nextTopicJoinStallEpisode += 1
          const timer = yield* Effect.sleep(normalized.topicJoinStallTimeout).pipe(
            Effect.andThen(
              offer({
                _tag: 'TopicJoinStallTimerFired',
                topic,
                episode
              })
            ),
            Effect.forkScoped({ startImmediately: true })
          )
          model.topicJoinStalls.set(topic, {
            episode,
            timer,
            reported: false
          })
        }
      })

    // A push whose topic left, rejoined, or lost its socket will never be
    // answered by the channel that received it; its caller hears so at once.
    const failPendingPushes = (model: RuntimeModel, topic: Topic | null) =>
      Effect.gen(function* () {
        for (const [ref, pending] of [...model.pendingPushesByRef.entries()]) {
          if (topic == null || pending.topic == topic) {
            model.pendingPushesByRef.delete(ref)
            yield* Deferred.fail(pending.deferred, new PushError('closed', null))
          }
        }
      })

    const clearPendingJoinRefsForTopic = (model: RuntimeModel, topic: Topic) =>
      Effect.gen(function* () {
        model.pendingJoinTopicByRef.forEach((pendingTopic, ref) => {
          if (pendingTopic == topic) {
            model.pendingJoinTopicByRef.delete(ref)
          }
        })
        yield* failPendingPushes(model, topic)
      })

    const scheduleReconnect = (model: RuntimeModel, generation: number, nextAttempt: number) =>
      Effect.gen(function* () {
        // Reconnect is modeled as a single in-flight timer. Any new schedule request replaces
        // the previous one to avoid overlapping reconnect attempts.
        yield* cancelConnectOpenTimeout(model)
        yield* cancelHeartbeatTimers(model)
        yield* interruptFiberIfAny(model.reconnectFiber)
        model.reconnectFiber = null

        const delayMs = computeBackoffMs({
          attempt: nextAttempt,
          baseDelayMs: normalized.reconnectBaseDelayMs,
          maxDelayMs: normalized.reconnectMaxDelayMs,
          jitter: normalized.reconnectJitter,
          random: yield* Random.next
        })

        model.reconnectAttempt = nextAttempt
        model.connection = {
          _tag: 'reconnecting',
          attempt: nextAttempt,
          nextDelayMs: delayMs
        }
        yield* publishConnectionState(model.connection)
        yield* Effect.logInfo(
          `[realtime-runtime] scheduling reconnect generation=${generation} attempt=${nextAttempt} delayMs=${delayMs}`
        )

        if (model.visibility == 'hidden') {
          // Hidden tabs pause reconnect scheduling and wait for lifecycle resume events.
          model.reconnectPausedByVisibility = true
          yield* Effect.logDebug(
            `[realtime-runtime] reconnect paused by visibility generation=${generation} attempt=${nextAttempt}`
          )
          return
        }

        model.reconnectPausedByVisibility = false
        const reconnectFiber = yield* Effect.sleep(Duration.millis(delayMs)).pipe(
          Effect.andThen(offer({ _tag: 'ReconnectTimerFired', generation })),
          Effect.forkScoped({ startImmediately: true })
        )
        model.reconnectFiber = reconnectFiber
      })

    const nextRef = (model: RuntimeModel): string => {
      const value = String(model.nextRef)
      model.nextRef += 1
      return value
    }

    const startJoin = (model: RuntimeModel, topic: Topic, attempt: number) =>
      Effect.gen(function* () {
        const transport = model.transport
        if (!transport || model.connection._tag != 'connected') {
          return
        }

        yield* cancelTopicTimers(model, topic)
        yield* clearPendingJoinRefsForTopic(model, topic)

        // phoenix.js sets join_ref to the same value as the join push ref.
        const ref = nextRef(model)
        const joinRef = ref

        model.topicStates.set(topic, {
          _tag: 'joining',
          attempt,
          joinRef,
          ref
        })
        model.pendingJoinTopicByRef.set(ref, topic)
        model.joinRefByTopic.set(topic, joinRef)
        yield* Effect.logInfo(
          `[realtime-runtime] join attempt topic=${topic} attempt=${attempt} generation=${model.generation}`
        )
        const joinSent = yield* transport.send(encodeJoinFrame(topic, joinRef, ref)).pipe(
          Effect.as(true),
          Effect.catchTag('ConnectTransportError', (error) =>
            Effect.gen(function* () {
              yield* Effect.logWarning(
                `[realtime-runtime] join send failed topic=${topic} attempt=${attempt} generation=${
                  model.generation
                } error=${formatUnknownError(error)}`
              )
              yield* scheduleTopicRetry(model, topic, attempt + 1)
              return false
            })
          )
        )

        if (!joinSent) {
          return
        }

        const timeoutFiber = yield* Effect.sleep(Duration.millis(normalized.joinTimeoutMs)).pipe(
          Effect.andThen(
            offer({
              _tag: 'JoinTimeoutFired',
              generation: model.generation,
              topic,
              ref
            })
          ),
          Effect.forkScoped({ startImmediately: true })
        )

        model.joinTimeoutFibers.set(topic, timeoutFiber)
      })

    const scheduleTopicRetry = (model: RuntimeModel, topic: Topic, attempt: number) =>
      Effect.gen(function* () {
        yield* cancelTopicTimers(model, topic)
        yield* clearPendingJoinRefsForTopic(model, topic)

        if (!model.desiredTopicRefCounts.has(topic)) {
          model.topicStates.set(topic, { _tag: 'idle', attempt: 0 })
          return
        }

        model.topicStates.set(topic, {
          _tag: 'retryWait',
          attempt
        })

        if (model.connection._tag != 'connected') {
          return
        }

        const delayMs = computeBackoffMs({
          attempt,
          baseDelayMs: normalized.joinRetryBaseDelayMs,
          maxDelayMs: normalized.joinRetryMaxDelayMs,
          jitter: normalized.reconnectJitter,
          random: yield* Random.next
        })

        const retryFiber = yield* Effect.sleep(Duration.millis(delayMs)).pipe(
          Effect.andThen(
            offer({
              _tag: 'JoinRetryTimerFired',
              generation: model.generation,
              topic,
              attempt
            })
          ),
          Effect.forkScoped({ startImmediately: true })
        )

        model.joinRetryFibers.set(topic, retryFiber)
      })

    const reconcileTopics = (model: RuntimeModel) =>
      Effect.gen(function* () {
        // Topic reconcile is the single place where desired subscriptions are turned into
        // join/leave wire operations. This keeps lifecycle transitions serialized and predictable.
        if (model.connection._tag != 'connected' || !model.transport) {
          return
        }

        for (const [topic, refCount] of model.desiredTopicRefCounts.entries()) {
          if (refCount <= 0) {
            continue
          }

          const topicState = model.topicStates.get(topic)
          if (!topicState || topicState._tag == 'idle') {
            const attempt = topicState?._tag == 'idle' ? topicState.attempt : 0
            yield* startJoin(model, topic, attempt)
          }
        }

        for (const [topic, topicState] of model.topicStates.entries()) {
          const refCount = model.desiredTopicRefCounts.get(topic) ?? 0
          if (refCount > 0) {
            continue
          }

          if (topicState._tag == 'joined' || topicState._tag == 'joining') {
            const leaveRef = nextRef(model)
            const joinRef = model.joinRefByTopic.get(topic) ?? null
            if (joinRef) {
              yield* model.transport
                .send(encodeLeaveFrame(topic, joinRef, leaveRef))
                .pipe(
                  Effect.catchTag('ConnectTransportError', (error) =>
                    Effect.logWarning(
                      `[realtime-runtime] leave send failed during topic reconcile topic=${topic}: ${error.message}`
                    )
                  )
                )
            }
          }

          yield* cancelTopicTimers(model, topic)
          model.topicStates.delete(topic)
          model.joinRefByTopic.delete(topic)
        }

        yield* publishConnectedTopics(model)
      })

    const dispatchToHandlers = (model: RuntimeModel, envelope: Envelope) =>
      Effect.gen(function* () {
        const handlers = model.handlersByTopic.get(envelope.topic)
        if (!handlers) {
          return
        }

        for (const handler of handlers.values()) {
          // Await each isolated handler so a subscriber observes frames in wire order.
          yield* handler(envelope).pipe(
            Effect.tapCause((cause) =>
              reportUnexpectedCause(
                `subscription handler failed topic=${envelope.topic} event=${envelope.event}`,
                cause
              )
            ),
            Effect.exit,
            Effect.asVoid
          )
        }
      })

    const startConnectionAttempt = (model: RuntimeModel, attempt: number) =>
      Effect.gen(function* () {
        model.generation += 1
        const generation = model.generation
        // A new generation must start from a clean timer slate to prevent stale callbacks
        // from mutating the current connection.
        yield* cancelConnectOpenTimeout(model)
        yield* cancelHeartbeatTimers(model)
        yield* interruptFiberIfAny(model.reconnectFiber)
        model.reconnectFiber = null
        model.reconnectPausedByVisibility = false

        if (attempt <= 1) {
          model.connection = {
            _tag: 'connecting',
            attempt
          }
        } else {
          model.connection = {
            _tag: 'reconnecting',
            attempt,
            nextDelayMs: 0
          }
        }
        yield* publishConnectionState(model.connection)

        // App-specific auth/CSRF param fetch happens on every connection attempt.
        // Failures are treated as reconnectable when connection is still desired.
        const paramsResult = yield* config.getConnectParams.pipe(
          Effect.map((value) => ({ _tag: 'Resolved' as const, value })),
          Effect.catchTag('ConnectParamsError', (error) =>
            Effect.gen(function* () {
              yield* Effect.logWarning(
                `[realtime-runtime] connect params failed generation=${generation} attempt=${attempt} error=${formatConnectParamsError(
                  error
                )}`
              )
              model.transport = null
              if (model.desiredConnected) {
                yield* scheduleReconnect(model, generation, attempt + 1)
              } else {
                model.connection = {
                  _tag: 'disconnected',
                  userInitiated: model.userInitiatedDisconnect
                }
                yield* publishConnectionState(model.connection)
              }
              return { _tag: 'Failed' as const }
            })
          )
        )
        if (paramsResult._tag == 'Failed') {
          return
        }

        const url = withQueryParams(config.endpoint, paramsResult.value)

        const transportResult = yield* makeTransport(url, {
          onOpen: offer({ _tag: 'SocketOpened', generation }),
          onMessage: (data) => offer({ _tag: 'SocketFrameReceived', generation, data }),
          onClose: (close) =>
            offer({
              _tag: 'SocketClosed',
              generation,
              code: close.code,
              reason: close.reason,
              wasClean: close.wasClean
            }),
          onError: (error) =>
            offer({
              _tag: 'SocketErrored',
              generation,
              error
            }),
          onDefect: (context, error) => {
            if (config.onDefect) {
              return config.onDefect({ context, error })
            }
            return Effect.logError(`[realtime-runtime] ${context}`, error)
          }
        }).pipe(
          Effect.map((value) => ({ _tag: 'Connected' as const, value })),
          Effect.catchTag('ConnectTransportError', (error) =>
            Effect.gen(function* () {
              yield* Effect.logWarning(
                `[realtime-runtime] transport factory failed generation=${generation} attempt=${attempt} error=${formatUnknownError(
                  error
                )}`
              )
              if (model.desiredConnected) {
                yield* scheduleReconnect(model, generation, attempt + 1)
              } else {
                model.connection = {
                  _tag: 'disconnected',
                  userInitiated: false
                }
                yield* publishConnectionState(model.connection)
              }
              return { _tag: 'Failed' as const }
            })
          )
        )

        if (transportResult._tag == 'Failed') {
          return
        }

        model.transport = transportResult.value
        yield* scheduleConnectOpenTimeout(model, generation)
      })

    const handleSocketClose = (
      model: RuntimeModel,
      command: Extract<RuntimeCommand, { _tag: 'SocketClosed' }>,
      opts?: {
        readonly skipReconnect?: boolean
      }
    ) =>
      Effect.gen(function* () {
        // Every transport callback is generation-tagged; stale callbacks are dropped here.
        if (command.generation != model.generation) {
          return
        }

        yield* cancelConnectOpenTimeout(model)
        yield* cancelHeartbeatTimers(model)
        model.transport = null

        for (const topic of model.topicStates.keys()) {
          yield* cancelTopicTimers(model, topic)
          if (model.desiredTopicRefCounts.has(topic)) {
            // Desired topics survive disconnects: transition to idle so reconcile can rejoin.
            const previousAttempt = model.topicStates.get(topic)
            let attempt = 0
            if (previousAttempt && 'attempt' in previousAttempt) {
              attempt = previousAttempt.attempt
            }
            model.topicStates.set(topic, {
              _tag: 'idle',
              attempt
            })
          } else {
            model.topicStates.delete(topic)
          }
        }

        model.pendingJoinTopicByRef.clear()
        yield* failPendingPushes(model, null)
        yield* publishConnectedTopics(model)

        if (model.desiredConnected && !opts?.skipReconnect) {
          yield* scheduleReconnect(model, model.generation, model.reconnectAttempt + 1)
          return
        }

        model.connection = {
          _tag: 'disconnected',
          userInitiated: model.userInitiatedDisconnect,
          lastCloseCode: command.code,
          lastCloseReason: command.reason
        }
        yield* publishConnectionState(model.connection)
      })

    const recoverConnectionFromLifecycleResume = (
      model: RuntimeModel,
      reason: string,
      opts?: {
        readonly forceIfConnecting?: boolean
        readonly forceIfConnected?: boolean
      }
    ) =>
      Effect.gen(function* () {
        if (!model.desiredConnected) {
          return
        }

        if (model.connection._tag == 'connecting' && !opts?.forceIfConnecting) {
          return
        }

        if (model.connection._tag == 'connected' && !opts?.forceIfConnected) {
          return
        }

        const staleTransport = model.transport
        if (staleTransport) {
          // Lifecycle resume should recover even when transport close callbacks never arrive.
          model.generation += 1
          yield* handleSocketClose(
            model,
            {
              _tag: 'SocketClosed',
              generation: model.generation,
              code: 1006,
              reason,
              wasClean: false
            },
            {
              skipReconnect: true
            }
          )
          yield* closeTransportWithoutBlocking(
            staleTransport,
            1000,
            reason,
            `transport close failed after ${reason}`
          )
          yield* startConnectionAttempt(model, Math.max(1, model.reconnectAttempt))
          return
        }

        yield* startConnectionAttempt(model, Math.max(1, model.reconnectAttempt))
      })

    const handleFrame = (
      model: RuntimeModel,
      command: Extract<RuntimeCommand, { _tag: 'SocketFrameReceived' }>
    ) =>
      Effect.gen(function* () {
        if (command.generation != model.generation) {
          return
        }

        const decoded = yield* decodePhoenixFrame(command.data).pipe(Effect.result)
        if (Result.isFailure(decoded)) {
          return
        }

        const frame = decoded.success
        // Heartbeat acks are transport-level and must not interact with topic state.
        if (
          frame.topic == 'phoenix' &&
          frame.event == 'phx_reply' &&
          frame.ref &&
          frame.ref == model.pendingHeartbeatRef
        ) {
          model.pendingHeartbeatRef = null
          yield* interruptFiberIfAny(model.heartbeatTimeoutFiber)
          model.heartbeatTimeoutFiber = null
          yield* scheduleHeartbeat(model)
          return
        }

        if (frame.event == 'phx_reply' && frame.ref) {
          const topic = model.pendingJoinTopicByRef.get(frame.ref)
          if (topic) {
            model.pendingJoinTopicByRef.delete(frame.ref)

            const topicState = model.topicStates.get(topic)
            if (!topicState || topicState._tag != 'joining' || topicState.ref != frame.ref) {
              // Stale reply for an outdated join attempt; phoenix.js ignores these.
              return
            }

            const status = readReplyStatus(frame.payload)
            if (status == 'ok') {
              yield* cancelTopicTimers(model, topic)
              model.topicStates.set(topic, {
                _tag: 'joined',
                joinRef: topicState.joinRef,
                ref: topicState.ref
              })
              yield* publishConnectedTopics(model)
              return
            }

            // Join replies that are explicit errors/timeouts or malformed statuses are retried
            // through the same bounded backoff path.
            yield* Effect.logWarning(
              `[realtime-runtime] join reply not_ok topic=${topic} status=${status} attempt=${topicState.attempt} generation=${model.generation}`
            )
            yield* scheduleTopicRetry(model, topic, topicState.attempt + 1)
            return
          }

          const pending = model.pendingPushesByRef.get(frame.ref)
          if (pending) {
            model.pendingPushesByRef.delete(frame.ref)
            const status = readReplyStatus(frame.payload)
            const response = readReplyResponse(frame.payload)
            if (status == 'ok') {
              yield* Deferred.succeed(pending.deferred, response)
            } else {
              yield* Deferred.fail(
                pending.deferred,
                new PushError(status == 'unknown' ? 'error' : status, response)
              )
            }
            return
          }
        }

        if (frame.joinRef) {
          // Mirrors phoenix channel.isMember: once a topic rejoins, old join_ref events are stale.
          const activeJoinRef = model.joinRefByTopic.get(frame.topic)
          if (!activeJoinRef || activeJoinRef != frame.joinRef) {
            return
          }
        }

        if (frame.event == 'phx_error') {
          yield* Effect.logWarning(
            `[realtime-runtime] topic phx_error topic=${frame.topic} generation=${model.generation}`
          )
          const topicState = model.topicStates.get(frame.topic)
          if (topicState) {
            const currentAttempt = 'attempt' in topicState ? topicState.attempt : 1
            yield* scheduleTopicRetry(model, frame.topic, currentAttempt + 1)
          }
          return
        }

        if (frame.event == 'phx_close') {
          const topicState = model.topicStates.get(frame.topic)
          if (!topicState) {
            return
          }

          yield* cancelTopicTimers(model, frame.topic)
          yield* clearPendingJoinRefsForTopic(model, frame.topic)
          model.joinRefByTopic.delete(frame.topic)

          if (model.desiredTopicRefCounts.has(frame.topic)) {
            // Declarative subscription model: if the topic is still desired, a server-side
            // close transitions to idle and reconcile drives a fresh join attempt.
            const currentAttempt = 'attempt' in topicState ? topicState.attempt : 1
            model.topicStates.set(frame.topic, {
              _tag: 'idle',
              attempt: currentAttempt
            })
            yield* publishConnectedTopics(model)
            yield* reconcileTopics(model)
          } else {
            model.topicStates.delete(frame.topic)
            yield* publishConnectedTopics(model)
          }
          return
        }

        yield* dispatchToHandlers(model, frameToEnvelope(frame))
      })

    const loop = (model: RuntimeModel): Effect.Effect<never> =>
      Effect.gen(function* () {
        // Single-threaded command loop: all mutable model updates happen here.
        // This is the core structured-concurrency invariant for runtime correctness.
        while (true) {
          const command = yield* Queue.take(commands)

          switch (command._tag) {
            case 'ConnectRequested': {
              model.desiredConnected = true
              model.userInitiatedDisconnect = false

              // connect() is idempotent: if runtime is already active, keep existing attempt.
              const isAlreadyActive =
                model.connection._tag == 'connected' ||
                model.connection._tag == 'connecting' ||
                model.connection._tag == 'reconnecting'

              if (!isAlreadyActive || !model.transport) {
                yield* startConnectionAttempt(model, Math.max(1, model.reconnectAttempt))
              }
              break
            }

            case 'DisconnectRequested': {
              // Explicit disconnect flips desiredConnected off first; every later lifecycle/socket
              // event observes this and must not re-arm reconnect.
              model.desiredConnected = false
              model.userInitiatedDisconnect = true
              model.reconnectAttempt = 0

              yield* cancelConnectOpenTimeout(model)
              yield* cancelHeartbeatTimers(model)
              yield* interruptFiberIfAny(model.reconnectFiber)
              model.reconnectFiber = null

              if (model.transport) {
                // Explicit disconnect is an ordering barrier: a following connect request
                // cannot open a replacement transport until closing this one has at least
                // completed (or reached the bounded timeout). Recovery paths stay non-blocking.
                yield* closeTransport(
                  model.transport,
                  1000,
                  'user disconnect',
                  'transport close failed during user disconnect'
                )
              }
              model.transport = null

              for (const topic of model.topicStates.keys()) {
                yield* cancelTopicTimers(model, topic)
              }
              model.topicStates.clear()
              model.pendingJoinTopicByRef.clear()
              yield* failPendingPushes(model, null)
              model.joinRefByTopic.clear()

              model.connection = {
                _tag: 'disconnected',
                userInitiated: true,
                lastCloseCode: 1000
              }
              yield* publishConnectionState(model.connection)
              yield* publishConnectedTopics(model)
              break
            }

            case 'LifecycleVisibilityChanged': {
              const wasHidden = model.visibility == 'hidden'
              model.visibility = command.visibility
              yield* Effect.logDebug(
                `[realtime-runtime] lifecycle visibility changed visibility=${command.visibility} wasHidden=${wasHidden} connectionState=${model.connection._tag} desiredConnected=${model.desiredConnected}`
              )
              if (command.visibility == 'hidden') {
                if (model.connection._tag == 'reconnecting') {
                  yield* interruptFiberIfAny(model.reconnectFiber)
                  model.reconnectFiber = null
                  model.reconnectPausedByVisibility = true
                }
                break
              }

              if (model.reconnectPausedByVisibility) {
                yield* recoverConnectionFromLifecycleResume(model, 'visibility resume')
              } else if (wasHidden && model.connection._tag == 'connecting') {
                // Visibility recovery should not wait for connect-open timeout when a
                // connecting transport becomes stale while hidden.
                yield* recoverConnectionFromLifecycleResume(model, 'visibility resume', {
                  forceIfConnecting: true
                })
              }
              break
            }

            case 'LifecyclePageHide': {
              yield* Effect.logInfo(
                `[realtime-runtime] lifecycle page hide persisted=${
                  command.persisted
                } connectionState=${model.connection._tag} desiredConnected=${
                  model.desiredConnected
                } hasTransport=${Boolean(model.transport)}`
              )
              model.visibility = 'hidden'
              if (model.connection._tag == 'reconnecting') {
                yield* interruptFiberIfAny(model.reconnectFiber)
                model.reconnectFiber = null
                model.reconnectPausedByVisibility = true
              }

              const hiddenTransport = model.transport
              if (!model.desiredConnected || !hiddenTransport) {
                break
              }

              // Mirror Phoenix pagehide semantics: teardown the active transport and defer
              // reconnect until the page is shown/visible again.
              model.generation += 1
              yield* handleSocketClose(model, {
                _tag: 'SocketClosed',
                generation: model.generation,
                code: 1000,
                reason: 'page hide',
                wasClean: true
              })
              yield* closeTransportWithoutBlocking(
                hiddenTransport,
                1000,
                'page hide',
                'transport close failed during page hide'
              )
              break
            }

            case 'LifecyclePageShow': {
              yield* Effect.logInfo(
                `[realtime-runtime] lifecycle page show persisted=${
                  command.persisted
                } connectionState=${model.connection._tag} desiredConnected=${
                  model.desiredConnected
                } hasTransport=${Boolean(model.transport)}`
              )
              const wasHidden = model.visibility == 'hidden'
              model.visibility = 'visible'
              // If pageshow arrives after hidden and runtime still reports connected, force
              // transport recycle. This mirrors Phoenix's pagehide/pageshow path where page
              // resume does not trust a long-lived hidden transport.
              yield* recoverConnectionFromLifecycleResume(model, 'page show resume', {
                forceIfConnected: wasHidden
              })
              break
            }

            case 'SocketOpened': {
              if (command.generation != model.generation) {
                break
              }
              yield* Effect.logDebug(
                `[realtime-runtime] socket opened generation=${command.generation}; clearing connect-open timeout`
              )
              yield* cancelConnectOpenTimeout(model)
              model.connection = {
                _tag: 'connected'
              }
              model.reconnectAttempt = 0
              yield* publishConnectionState(model.connection)
              yield* scheduleHeartbeat(model)
              yield* reconcileTopics(model)
              break
            }

            case 'SocketClosed': {
              yield* Effect.logInfo(
                `[realtime-runtime] socket closed event generation=${command.generation} code=${
                  command.code
                } reason=${command.reason ?? '-'} wasClean=${command.wasClean}`
              )
              yield* handleSocketClose(model, command)
              break
            }

            case 'SocketErrored': {
              if (command.generation != model.generation) {
                break
              }
              const desiredTopics = [...model.desiredTopicRefCounts.keys()].sort((a, b) =>
                a.localeCompare(b)
              )
              const joinedTopics = sortedJoinedTopics(model)
              const socketError: RealtimeSocketErroredEvent['error'] = PlatformSocket.isSocketError(
                command.error
              )
                ? command.error.reason._tag == 'SocketCloseError'
                  ? {
                      tag: 'SocketCloseError',
                      reason: command.error.reason.closeReason,
                      message: command.error.message,
                      code: command.error.reason.code,
                      closeReason: command.error.reason.closeReason
                    }
                  : {
                      tag: 'SocketGenericError',
                      reason: command.error.reason._tag,
                      message: command.error.message,
                      cause: formatUnknownError(command.error.reason)
                    }
                : {
                    tag: 'UnknownError',
                    message: formatUnknownError(command.error)
                  }
              const hasTransport = Boolean(model.transport)
              const socketErroredEvent: RealtimeSocketErroredEvent = {
                generation: command.generation,
                hasTransport,
                error: socketError,
                connectionState: model.connection._tag,
                desiredConnected: model.desiredConnected,
                visibility: model.visibility,
                desiredTopics,
                joinedTopics
              }

              yield* Effect.logWarning(
                `[realtime-runtime] socket errored generation=${command.generation} hasTransport=${hasTransport}`
              ).pipe(
                Effect.annotateLogs({
                  socketError: socketErroredEvent
                })
              )
              if (config.onSocketErrored) {
                yield* config.onSocketErrored(socketErroredEvent).pipe(
                  Effect.tapCause((cause) =>
                    reportUnexpectedCause('onSocketErrored callback failed', cause)
                  ),
                  Effect.exit
                )
              }
              yield* cancelHeartbeatTimers(model)
              if (!model.desiredConnected) {
                break
              }

              const erroredTransport = model.transport
              if (erroredTransport) {
                // Teardown/reconnect is driven locally for deterministic recovery even if transport
                // close callbacks are delayed or never delivered by a custom transport.
                model.generation += 1
                yield* handleSocketClose(model, {
                  _tag: 'SocketClosed',
                  generation: model.generation,
                  code: 1006,
                  reason: 'socket error',
                  wasClean: false
                })
                yield* closeTransportWithoutBlocking(
                  erroredTransport,
                  1000,
                  'socket error',
                  'transport close failed after socket error'
                )
              } else {
                yield* scheduleReconnect(model, model.generation, model.reconnectAttempt + 1)
              }
              break
            }

            case 'SocketFrameReceived': {
              yield* handleFrame(model, command)
              break
            }

            case 'ReconnectTimerFired': {
              if (command.generation != model.generation) {
                break
              }
              if (!model.desiredConnected) {
                break
              }
              yield* Effect.logDebug(
                `[realtime-runtime] reconnect timer fired generation=${command.generation} attempt=${model.reconnectAttempt}`
              )
              yield* startConnectionAttempt(model, Math.max(1, model.reconnectAttempt))
              break
            }

            case 'ConnectOpenTimeoutFired': {
              if (command.generation != model.generation) {
                break
              }

              model.connectOpenTimeoutFiber = null

              if (!model.desiredConnected) {
                break
              }

              if (
                model.connection._tag != 'connecting' &&
                model.connection._tag != 'reconnecting'
              ) {
                break
              }

              const stalledTransport = model.transport
              yield* Effect.logWarning(
                `[realtime-runtime] connect-open timeout fired generation=${
                  command.generation
                } state=${model.connection._tag} reconnectAttempt=${
                  model.reconnectAttempt
                } hasTransport=${Boolean(stalledTransport)}`
              )
              if (stalledTransport) {
                // Drive timeout recovery through local close semantics so topic state and
                // reconnect scheduling stay consistent even if transport callbacks never arrive.
                model.generation += 1
                yield* handleSocketClose(model, {
                  _tag: 'SocketClosed',
                  generation: model.generation,
                  code: 1006,
                  reason: 'connect open timeout',
                  wasClean: false
                })
                yield* closeTransportWithoutBlocking(
                  stalledTransport,
                  1000,
                  'connect open timeout',
                  'transport close failed after connect open timeout'
                )
              } else {
                yield* scheduleReconnect(model, model.generation, model.reconnectAttempt + 1)
              }
              break
            }

            case 'HeartbeatTimerFired': {
              if (command.generation != model.generation) {
                break
              }
              if (!model.desiredConnected || model.connection._tag != 'connected') {
                break
              }
              model.heartbeatFiber = null
              yield* sendHeartbeat(model)
              break
            }

            case 'HeartbeatTimeoutFired': {
              if (command.generation != model.generation) {
                break
              }
              if (command.ref != model.pendingHeartbeatRef) {
                break
              }

              model.pendingHeartbeatRef = null
              yield* interruptFiberIfAny(model.heartbeatTimeoutFiber)
              model.heartbeatTimeoutFiber = null

              const timedOutTransport = model.transport
              if (timedOutTransport) {
                // Heartbeat timeout must not depend on transport close callbacks; recover locally.
                model.generation += 1
                yield* handleSocketClose(model, {
                  _tag: 'SocketClosed',
                  generation: model.generation,
                  code: 1006,
                  reason: 'heartbeat timeout',
                  wasClean: false
                })
                yield* closeTransportWithoutBlocking(
                  timedOutTransport,
                  1000,
                  'heartbeat timeout',
                  'transport close failed after heartbeat timeout'
                )
              } else if (model.desiredConnected) {
                yield* scheduleReconnect(model, model.generation, model.reconnectAttempt + 1)
              }
              break
            }

            case 'JoinTimeoutFired': {
              if (command.generation != model.generation) {
                break
              }

              const topicState = model.topicStates.get(command.topic)
              if (!topicState || topicState._tag != 'joining' || topicState.ref != command.ref) {
                break
              }

              yield* Effect.logDebug(
                `[realtime-runtime] join timeout topic=${command.topic} ref=${command.ref} attempt=${topicState.attempt} generation=${command.generation}`
              )
              yield* scheduleTopicRetry(model, command.topic, topicState.attempt + 1)
              break
            }

            case 'JoinRetryTimerFired': {
              if (command.generation != model.generation) {
                break
              }

              const topicState = model.topicStates.get(command.topic)
              if (
                !topicState ||
                topicState._tag != 'retryWait' ||
                topicState.attempt != command.attempt
              ) {
                break
              }

              yield* Effect.logDebug(
                `[realtime-runtime] join retry timer fired topic=${command.topic} attempt=${command.attempt} generation=${command.generation}`
              )
              model.joinRetryFibers.delete(command.topic)
              model.topicStates.set(command.topic, {
                _tag: 'idle',
                attempt: command.attempt
              })
              yield* reconcileTopics(model)
              break
            }

            case 'TopicJoinStallTimerFired': {
              const topicJoinStall = config.topicJoinStall
              const timeout = normalized.topicJoinStallTimeout
              const stall = model.topicJoinStalls.get(command.topic)

              if (
                !topicJoinStall ||
                !timeout ||
                !stall ||
                stall.episode != command.episode ||
                model.visibility == 'hidden' ||
                !model.desiredTopicRefCounts.has(command.topic) ||
                model.topicStates.get(command.topic)?._tag == 'joined' ||
                stall.reported
              ) {
                break
              }

              model.topicJoinStalls.set(command.topic, {
                ...stall,
                timer: null,
                reported: true
              })
              const stalledEvent: RealtimeTopicJoinStalledEvent = {
                topic: command.topic,
                timeoutMs: Duration.toMillis(timeout),
                generation: model.generation,
                connectionState: model.connection._tag,
                desiredConnected: model.desiredConnected,
                visibility: model.visibility,
                desiredTopics: [...model.desiredTopicRefCounts.keys()].sort((a, b) =>
                  a.localeCompare(b)
                ),
                joinedTopics: sortedJoinedTopics(model)
              }

              yield* topicJoinStall.onStalled(stalledEvent).pipe(
                Effect.tapCause((cause) =>
                  reportUnexpectedCause('topicJoinStall callback failed', cause)
                ),
                Effect.exit
              )
              break
            }

            case 'PushRequested': {
              const topicState = model.topicStates.get(command.topic)
              const transport = model.transport
              if (
                !transport ||
                model.connection._tag != 'connected' ||
                !topicState ||
                topicState._tag != 'joined'
              ) {
                yield* Deferred.fail(command.deferred, new PushError('not_joined', null))
                break
              }

              const ref = nextRef(model)
              model.pendingPushesByRef.set(ref, { topic: command.topic, deferred: command.deferred })
              const sent = yield* transport
                .send(
                  encodePushFrame(command.topic, topicState.joinRef, ref, command.event, command.payload)
                )
                .pipe(
                  Effect.as(true),
                  Effect.catchTag('ConnectTransportError', () => Effect.succeed(false))
                )
              if (!sent) {
                model.pendingPushesByRef.delete(ref)
                yield* Deferred.fail(command.deferred, new PushError('closed', null))
              }
              break
            }

            case 'RejoinRequested': {
              const joinRef = model.joinRefByTopic.get(command.topic)
              if (model.transport && joinRef) {
                yield* model.transport
                  .send(encodeLeaveFrame(command.topic, joinRef, nextRef(model)))
                  .pipe(
                    Effect.catchTag('ConnectTransportError', (error) =>
                      Effect.logWarning(`[realtime-runtime] rejoin leave failed: ${error.message}`)
                    )
                  )
              }
              yield* cancelTopicTimers(model, command.topic)
              yield* clearPendingJoinRefsForTopic(model, command.topic)
              model.joinRefByTopic.delete(command.topic)
              model.topicStates.delete(command.topic)
              // Publish the leave before starting the replacement join. Session
              // owners discard their values and resubscribe when it succeeds.
              yield* publishConnectedTopics(model)
              yield* reconcileTopics(model)
              break
            }

            case 'AddSubscription': {
              const handlers = model.handlersByTopic.get(command.topic) ?? new Map()
              handlers.set(command.subscriptionId, command.handler)
              model.handlersByTopic.set(command.topic, handlers)

              const currentCount = model.desiredTopicRefCounts.get(command.topic) ?? 0
              model.desiredTopicRefCounts.set(command.topic, currentCount + 1)
              if (!model.topicStates.has(command.topic)) {
                model.topicStates.set(command.topic, {
                  _tag: 'idle',
                  attempt: 0
                })
              }

              yield* reconcileTopics(model)
              break
            }

            case 'RemoveSubscription': {
              const handlers = model.handlersByTopic.get(command.topic)
              if (handlers) {
                handlers.delete(command.subscriptionId)
                if (handlers.size == 0) {
                  model.handlersByTopic.delete(command.topic)
                }
              }

              const currentCount = model.desiredTopicRefCounts.get(command.topic) ?? 0
              if (currentCount <= 1) {
                model.desiredTopicRefCounts.delete(command.topic)

                const topicState = model.topicStates.get(command.topic)
                if (topicState && (topicState._tag == 'joining' || topicState._tag == 'joined')) {
                  const joinRef = model.joinRefByTopic.get(command.topic)
                  if (joinRef && model.transport && model.connection._tag == 'connected') {
                    const leaveRef = nextRef(model)
                    // Leave is best-effort: local desired state is authoritative even if wire leave fails.
                    yield* model.transport
                      .send(encodeLeaveFrame(command.topic, joinRef, leaveRef))
                      .pipe(
                        Effect.catchTag('ConnectTransportError', (error) =>
                          Effect.logWarning(
                            `[realtime-runtime] leave send failed during topic unsubscribe topic=${command.topic}: ${error.message}`
                          )
                        )
                      )
                  }
                }

                yield* cancelTopicTimers(model, command.topic)
                model.topicStates.delete(command.topic)
                yield* clearPendingJoinRefsForTopic(model, command.topic)
                model.joinRefByTopic.delete(command.topic)
                yield* publishConnectedTopics(model)
              } else {
                model.desiredTopicRefCounts.set(command.topic, currentCount - 1)
              }
              break
            }
          }

          yield* reconcileTopicJoinStalls(model)
        }
      }) as Effect.Effect<never>

    const model = makeInitialModel()
    if (config.lifecycle) {
      model.visibility = yield* config.lifecycle.currentVisibility
    }

    yield* loop(model).pipe(
      Effect.tapCause((cause) =>
        reportUnexpectedCause('command loop terminated unexpectedly', cause)
      ),
      Effect.forkScoped({ startImmediately: true })
    )

    if (config.lifecycle) {
      // Lifecycle stream is supervised by socket scope; shutdown interrupts it automatically.
      yield* config.lifecycle.events.pipe(
        Stream.runForEach((event) => offerLifecycleEvent(event)),
        Effect.tapCause((cause) =>
          reportUnexpectedCause('lifecycle event stream terminated unexpectedly', cause)
        ),
        Effect.forkScoped({ startImmediately: true })
      )
    }

    const connect = offer({ _tag: 'ConnectRequested' })
    const disconnect = offer({ _tag: 'DisconnectRequested' })

    const subscribe: RealtimeSocket['subscribe'] = (topic, onEvent) =>
      Effect.acquireRelease(
        Effect.gen(function* () {
          const subscriptionId = yield* Ref.modify(subscriptionIdRef, (value) => [value, value + 1])
          yield* offer({
            _tag: 'AddSubscription',
            topic,
            subscriptionId,
            handler: onEvent
          })
          return subscriptionId
        }),
        (subscriptionId) =>
          offer({
            _tag: 'RemoveSubscription',
            topic,
            subscriptionId
          })
      ).pipe(Effect.asVoid)

    const push: RealtimeSocket['push'] = (topic, event, payload) =>
      Effect.gen(function* () {
        const deferred = yield* Deferred.make<unknown, PushError>()
        yield* offer({ _tag: 'PushRequested', topic, event, payload, deferred })
        return yield* Deferred.await(deferred)
      })

    return {
      connect,
      disconnect,
      rejoin: (topic) => offer({ _tag: 'RejoinRequested', topic }),
      subscribe,
      push,
      connectionStates: SubscriptionRef.changes(connectionStateRef),
      connectedTopics: SubscriptionRef.changes(connectedTopicsRef)
    } satisfies RealtimeSocket
  })
}

function withQueryParams(endpoint: string, params: Readonly<Record<string, string>>): string {
  const url = new URL(endpoint)
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value)
  }
  return url.toString()
}

function readReplyResponse(payload: unknown): unknown {
  if (typeof payload != 'object' || payload == null) {
    return null
  }
  return (payload as { response?: unknown }).response ?? null
}

function readReplyStatus(payload: unknown): 'ok' | 'error' | 'timeout' | 'unknown' {
  if (typeof payload != 'object' || payload == null) {
    return 'unknown'
  }
  if (!('status' in payload)) {
    return 'unknown'
  }

  const value = (payload as { status?: unknown }).status
  if (value == 'ok' || value == 'error' || value == 'timeout') {
    return value as 'ok' | 'error' | 'timeout'
  }

  return 'unknown'
}

function formatConnectParamsError(error: ConnectParamsError): string {
  const cause = error.cause
  if (!cause) {
    return error.message
  }

  if (isTransportError(cause)) {
    const failure = cause.classification
    if (!failure) {
      return `${error.message}; cause=TransportError; request=${cause.request.method} ${cause.request.url}`
    }

    switch (failure.type) {
      case 'network':
        return `${error.message}; cause=TransportError; type=network; request=${failure.method} ${failure.url}; failedToFetch=${failure.isFailedToFetch}; webkitLoadFailed=${failure.isWebKitLoadFailed}; firefoxNetworkError=${failure.isFirefoxNetworkError}; message=${failure.message}`
      case 'timeout':
        return `${error.message}; cause=TransportError; type=timeout; request=${failure.method} ${
          failure.url
        }; timeoutMs=${failure.timeoutMs ?? 'unknown'}; message=${failure.message}`
      case 'abort':
        return `${error.message}; cause=TransportError; type=abort; request=${failure.method} ${failure.url}; signalAborted=${failure.signalAborted}; message=${failure.message}`
      case 'invalidRequest':
        return `${error.message}; cause=TransportError; type=invalidRequest; request=${failure.method} ${failure.url}; message=${failure.message}`
      case 'http':
        return `${error.message}; cause=TransportError; type=http; request=${failure.method} ${failure.url}; status=${failure.status}; message=${failure.message}`
      case 'unknown':
        return `${error.message}; cause=TransportError; type=unknown; request=${failure.method} ${failure.url}; message=${failure.message}`
    }
  }

  if (isHttpStatusError(cause)) {
    return `${error.message}; cause=HttpStatusError; request=${cause.request.method} ${cause.request.url}; status=${cause.response.status}`
  }

  if (isTimeoutError(cause)) {
    return `${error.message}; cause=TimeoutError; request=${cause.request.method} ${
      cause.request.url
    }; timeoutMs=${cause.timeoutMs ?? 'unknown'}`
  }

  if (isInvalidRequestError(cause)) {
    return `${error.message}; cause=InvalidRequestError; request=${cause.request.method} ${cause.request.url}; reason=${cause.reason}`
  }

  if (isInvalidResponseError(cause)) {
    return `${error.message}; cause=InvalidResponseError; request=${cause.request.method} ${cause.request.url}; reason=${cause.reason}`
  }

  if (cause instanceof Error) {
    return `${error.message}; cause=${cause.name}: ${cause.message}`
  }

  return `${error.message}; cause=${formatUnknownError(cause)}`
}

function formatUnknownError(error: unknown): string {
  return Cause.pretty(Cause.die(error))
}
